import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL, EXPLORER_API_BASE_URL } from '../config/api';

// 🐣🏠 Settle-in guide (Nina 08-14: "watch your agent settle in" — they can
// explore their space while waiting for their explore session).
//
// After the import wizard auto-creates an agent's home space, it stashes
// {agent_id, agent_name, game_id, ts} in localStorage and lands the owner in
// /game-step. This app-level card picks the stash up and turns the dead air
// (theme generating + maiden-voyage queue) into a guided first-five-minutes:
//   1. say hi to your agent (it's seated and talking already)
//   2. the space is painting itself (theme/art generation runs ~a minute)
//   3. live maiden-walk status: #N in line → LIVE (watch button) → replay
//
// Deliberately mounted app-wide (CaptureTray pattern) with ZERO GameStepPage
// coupling — that file is 45K lines and co-owned. Renders nothing unless the
// stash matches the current /game-step?gameId=. ✕ clears the stash for good;
// stashes older than 24h self-expire.

const STASH_KEY = 'spaces_settle_in';
const QUEUE_POLL_MS = 20000;
const STATUS_POLL_MS = 15000;
const STASH_TTL_MS = 24 * 60 * 60 * 1000;

const readStash = () => {
  try {
    const raw = localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const stash = JSON.parse(raw);
    if (!stash || !stash.agent_id || !stash.game_id) return null;
    if (!stash.ts || Date.now() - stash.ts > STASH_TTL_MS) {
      localStorage.removeItem(STASH_KEY);
      return null;
    }
    return stash;
  } catch { return null; }
};

const SettleInGuide = () => {
  const location = useLocation();
  const [stash, setStash] = useState(readStash);
  // Maiden-walk beat: 'queued' #N | 'live' | 'replay' | null (nothing known)
  const [walk, setWalk] = useState(null);
  // Painting beat (v2): flips on GameStepPage's 'spaces-space-theme-ready'
  // broadcast (real ui_theme presence off its status poll) instead of a clock.
  const [painted, setPainted] = useState(false);

  // Re-read on navigation + same-tab signal from the import wizard.
  useEffect(() => { setStash(readStash()); }, [location.key]);
  useEffect(() => {
    const onChange = () => setStash(readStash());
    window.addEventListener('spaces-settle-in-changed', onChange);
    return () => window.removeEventListener('spaces-settle-in-changed', onChange);
  }, []);
  useEffect(() => {
    const onThemeReady = (e) => {
      const current = readStash();
      if (current && e?.detail?.gameId === current.game_id) setPainted(true);
    };
    window.addEventListener('spaces-space-theme-ready', onThemeReady);
    return () => window.removeEventListener('spaces-space-theme-ready', onThemeReady);
  }, []);

  const gameIdInUrl = useMemo(() => {
    if (location.pathname !== '/game-step') return null;
    return new URLSearchParams(location.search).get('gameId');
  }, [location.pathname, location.search]);

  const active = !!(stash && gameIdInUrl && gameIdInUrl === stash.game_id);

  // Queue + replay poll (jsg backend, public read — same feed the /explore
  // strip uses). Only while the card is showing.
  useEffect(() => {
    if (!active) return undefined;
    let gone = false;
    let timer = null;
    const load = () => {
      fetch(`${API_BASE_URL}/api/expeditionQueue/public`)
        .then((r) => r.json())
        .then((d) => {
          if (gone || !d || !d.success) return;
          const queue = Array.isArray(d.queue) ? d.queue : [];
          const idx = queue.findIndex((q) => q.agent_id === stash.agent_id);
          if (idx >= 0) {
            setWalk({ kind: 'queued', position: idx + 1 });
            return;
          }
          const feed = Array.isArray(d.feed) ? d.feed : [];
          const replay = feed.find((f) => f.agent_id === stash.agent_id && f.session);
          // Never downgrade a live state from this slower poll; live is owned
          // by the status poll below and clears itself.
          setWalk((prev) => (prev && prev.kind === 'live')
            ? prev
            : (replay ? { kind: 'replay', session: replay.session } : null));
        })
        .catch(() => { /* keep last known state */ })
        .finally(() => { if (!gone) timer = setTimeout(load, QUEUE_POLL_MS); });
    };
    load();
    return () => { gone = true; if (timer) clearTimeout(timer); };
  }, [active, stash]);

  // Live poll (explorer stack): is OUR chibi the one on stream right now?
  useEffect(() => {
    if (!active) return undefined;
    let gone = false;
    let timer = null;
    const load = () => {
      fetch(`${EXPLORER_API_BASE_URL}/explorer/status`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (gone || !d) return;
          if (d.is_live && d.agent_id === stash.agent_id) {
            setWalk({ kind: 'live' });
          } else {
            setWalk((prev) => (prev && prev.kind === 'live' ? null : prev));
          }
        })
        .catch(() => { /* explorer stack hiccup — keep last state */ })
        .finally(() => { if (!gone) timer = setTimeout(load, STATUS_POLL_MS); });
    };
    load();
    return () => { gone = true; if (timer) clearTimeout(timer); };
  }, [active, stash]);

  if (!active) return null;

  const dismiss = () => {
    try { localStorage.removeItem(STASH_KEY); } catch { /* private mode */ }
    setStash(null);
  };

  const name = stash.agent_name || 'Your agent';
  // Painting beat: real signal first (painted), 5-min clock only as the
  // fallback hide for spaces whose theme never lands.
  const stillPainting = !painted && Date.now() - stash.ts < 5 * 60 * 1000;

  return (
    <div style={S.card}>
      <div style={S.head}>
        <span style={S.headTitle}>🐣 {name} is settling in</span>
        <button onClick={dismiss} aria-label="Dismiss" style={S.close}>✕</button>
      </div>
      <div style={S.beat}>
        <span style={S.beatEmoji}>👋</span>
        <span>
          {name} just moved in and is ready to chat.{' '}
          <button
            style={S.sayHiBtn}
            onClick={() => {
              try {
                window.dispatchEvent(new CustomEvent('spaces-settle-in-say-hi', { detail: { agentName: name } }));
              } catch { /* no-op */ }
            }}
          >
            Say hi 👋
          </button>
        </span>
      </div>
      {stillPainting && (
        <div style={S.beat}>
          <span style={S.beatEmoji}>🎨</span>
          <span>The space is painting itself. Art and theme land in a minute or two.</span>
        </div>
      )}
      {painted && (
        <div style={S.beat}>
          <span style={S.beatEmoji}>✨</span>
          <span>The space finished painting. Have a look around!</span>
        </div>
      )}
      {walk && walk.kind === 'queued' && (
        <div style={S.beat}>
          <span style={S.beatEmoji}>🛫</span>
          <span>#{walk.position} in line for their first walk outside. You will see it here.</span>
        </div>
      )}
      {walk && walk.kind === 'live' && (
        <button
          style={S.liveBtn}
          onClick={() => window.open('/explorer/theater', '_blank', 'noopener')}
        >
          🔴 {name} is LIVE on their first walk. Watch!
        </button>
      )}
      {walk && walk.kind === 'replay' && (
        <button
          style={S.replayBtn}
          onClick={() => window.open(`/explorer/theater?replay=${encodeURIComponent(walk.session)}`, '_blank', 'noopener')}
        >
          📼 Watch {name}&rsquo;s first walk
        </button>
      )}
    </div>
  );
};

const S = {
  card: {
    position: 'fixed', right: 14, bottom: 14, zIndex: 100008,
    width: 'min(300px, calc(100vw - 28px))',
    background: 'rgba(18, 16, 28, 0.96)', color: '#f0ecff',
    border: '1px solid rgba(139, 92, 246, 0.45)', borderRadius: 14,
    padding: '12px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
    fontSize: 13, lineHeight: 1.45,
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  headTitle: { flex: 1, fontWeight: 800, fontSize: 13.5 },
  close: {
    background: 'none', border: 'none', color: 'rgba(240,236,255,0.55)',
    cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1,
  },
  beat: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 },
  sayHiBtn: {
    padding: '3px 10px', marginLeft: 2, borderRadius: 999,
    border: '1px solid rgba(139,92,246,0.6)', background: 'rgba(139,92,246,0.16)',
    color: '#ddd2ff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
  },
  beatEmoji: { flexShrink: 0 },
  liveBtn: {
    display: 'block', width: '100%', marginTop: 4, padding: '9px 12px',
    borderRadius: 10, border: '1px solid rgba(239,68,68,0.7)',
    background: 'rgba(239,68,68,0.16)', color: '#ffd7d7',
    fontWeight: 800, fontSize: 13, cursor: 'pointer',
  },
  replayBtn: {
    display: 'block', width: '100%', marginTop: 4, padding: '9px 12px',
    borderRadius: 10, border: '1px solid rgba(139,92,246,0.6)',
    background: 'rgba(139,92,246,0.16)', color: '#ddd2ff',
    fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
};

export default SettleInGuide;
